import axios from "axios";
import dayjs from "dayjs";
import { createTaskService } from "./taskService";
import {
  addEmailActionService,
  addEmailFileIdService,
  updateDigestEmailsService,
} from "./digestService";
import { moveEmailService } from "./emailService";
import { getMailWriteContext, refuseMailWrite } from "./mailProvider";
import { isFiledInTriageDestination } from "mail-destination-config";

const API_BASE_URL = process.env.REACT_APP_API_URL;

/**
 * Mark an email as read
 * @param {Object} params - Parameters for marking an email as read
 * @param {string} params.emailId - ID of the email to mark as read
 * @param {function} params.dispatch - Redux dispatch function
 * @param {Object} params.currentDigest - Current digest object
 * @returns {Promise<Object>} Response from the mark email as read API
 *
 */
export const markEmailAsRead = async ({
  emailId,
  mailIntegrationId,
  dispatch,
  currentDigest,
  updateDigestAction,
  getToken,
}) => {
  const blocked = refuseMailWrite(
    "mark_email_as_read",
    getMailWriteContext({ currentDigest, emailId })
  );
  if (blocked) return blocked;

  try {
    const token = await getToken();

    const response = await axios
      .post(
        `${API_BASE_URL}/api/email/mark_email_as_read`,
        {
          emailId: emailId,
          ...(mailIntegrationId ? { mailIntegrationId } : {}),
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      )
      .then((response) => {
        return response.data;
      });

    if (!response.data.id) {
      throw new Error("Failed to mark email as read");
    }

    dispatch(
      updateDigestAction({
        ...currentDigest,
        content: currentDigest.content.map((item) =>
          item.id === emailId
            ? {
                ...item,
                isUnread: false,
              }
            : item
        ),
      })
    );

    return response.data;
  } catch (error) {
    console.error("Error marking email as read:", error);
    throw error;
  }
};

/**
 * Reply to an email
 * @param {Object} params - Parameters for replying to an email
 * @param {string} params.email - The email object
 * @param {string} params.replyText - The text of the reply
 * @param {function} params.emailActionData - Email action data
 * @param {function} params.getToken - Get token function
 * @returns {Promise<Object>} Response from the email reply API
 */
export const replyToEmail = async ({
  email,
  mailIntegrationId,
  replyText,
  emailActionData,
  currentDigest,
  getToken,
}) => {
  const blocked = refuseMailWrite(
    "reply_to_email",
    getMailWriteContext({ currentDigest, email })
  );
  if (blocked) return blocked;

  try {
    const token = await getToken();

    const response = await axios.post(
      `${API_BASE_URL}/api/email/reply_to_email`,
      {
        emailId: email.id,
        fromEmailAddresses: [email.from],
        subject: email.subject,
        replyBody: replyText,
        ...(mailIntegrationId ? { mailIntegrationId } : {}),
      },
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      }
    )
    .then((response) => {
      return response.data;
    });

    if (!response.executedAction.id) {
      throw new Error("Failed to reply to email");
    }

    await addEmailActionService(getToken, { emailId: email.id, emailActions: emailActionData, mailIntegrationId });

    return response.executedAction;
  } catch (error) {
    console.error("Error replying to email:", error);
    throw error;
  }
};

/**
 * Move an email to a specific label
 * @param {Object} params - Parameters for moving an email
 * @param {string} params.emailId - ID of the email to move
 * @param {function} params.dispatch - Redux dispatch function
 * @param {Object} params.currentDigest - Current digest object
 * @param {function} params.setIsVisible - State setter for visibility
 * @returns {Promise<Object>} Response from the move email API
 */
export const moveEmail = async ({
  emailId,
  mailIntegrationId,
  destinationKey,
  emailActionData,
  currentDigest,
  getToken,
}) => {
  const writeContext = getMailWriteContext({ currentDigest, emailId });
  const blocked = refuseMailWrite("move_email", writeContext);
  if (blocked) return blocked;

  try {
    const response = await moveEmailService(getToken, {
      emailId,
      destinationKey,
      emailActionData,
      mailIntegrationId,
      ...writeContext,
    });

    if (!response.executedAction.id) {
      throw new Error("Failed to move email");
    }

    await addEmailActionService(getToken, { emailId, mailIntegrationId, emailActions: {
      ...emailActionData,
      metaData: {
        ...emailActionData.metaData,
        destinationKey,
      },
    }});

    return response.data;
  } catch (error) {
    console.error("Error moving email:", error);
    throw error;
  }
};

/**
 * Archive an email
 * @param {Object} params - Parameters for archiving an email
 * @param {string} params.emailId - ID of the email to archive
 * @param {function} params.emailActionData - Email action data
 * @param {Object} params.currentDigest - Current digest object
 * @param {function} params.getToken - Get token function
 * @returns {Promise<Object>} Response from the archive email API
 */
export const archiveEmail = async ({
  emailId,
  mailIntegrationId,
  emailActionData,
  currentDigest,
  getToken,
}) => {
  const blocked = refuseMailWrite(
    "archive_email",
    getMailWriteContext({ currentDigest, emailId })
  );
  if (blocked) return blocked;

  try {
    const token = await getToken();

    const response = await axios
      .post(
        `${API_BASE_URL}/api/email/archive_email`,
        {
          emailId,
          ...(mailIntegrationId ? { mailIntegrationId } : {}),
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      )
      .then((response) => {
        return response.data;
      });

    if (!response.executedAction.id) {
      throw new Error("Failed to archive email");
    }

    await addEmailActionService(getToken, { emailId, emailActions: emailActionData, mailIntegrationId });
    await updateDigestEmailsService(getToken, currentDigest.id, [
      {
        ...currentDigest.content.find((item) => item.id === emailId),
        archived: true,
      },
    ]);

    return response.executedAction;
  } catch (error) {
    console.error("Error archiving email:", error);
    throw error;
  }
};

/**
 * Create a task
 * @param {Object} params - Parameters for creating a task
 * @param {string} params.email - Email object
 * @param {function} params.dispatch - Redux dispatch function
 * @param {Object} params.emailActionData - Email action data
 * @param {function} params.createTaskAction - Create task action
 * @param {function} params.getToken - Get token function
 * @param {string} params.userId - User ID
 * @returns {Promise<Object>} Response from the create task API
 */
export const createEmailTask = async ({
  email,
  dispatch,
  emailActionData,
  createTaskAction,
  getToken,
  userId,
  mailIntegrationId,
}) => {
  try {
    const newTask = {
      id: `task_${Math.random().toString(36).substring(2, 15)}`,
      title:
        emailActionData.metaData?.title ||
        `Untitled Task created from email: ${dayjs().format("YYYY-MM-DD")}`,
      description:
        emailActionData.metaData?.content ||
        `Task created from email: ${email.subject}`,
      startBy: null,
      endBy: emailActionData.metaData?.endBy || null,
      estimation: 0,
      sortOrder: 0,
      metaData: {
        emailId: email.id,
        emailSubject: email.subject,
        emailFrom: email.from,
      },
    };

    const createdTask = await createTaskService(getToken, userId, newTask);
    emailActionData.metaData.taskId = createdTask.id;
    await addEmailActionService(getToken, { emailId: email.id, emailActions: emailActionData, mailIntegrationId });

    dispatch(createTaskAction(createdTask));

    return createdTask.id; // Return the ID of the newly created task
  } catch (error) {
    console.error("Error creating task:", error);
    throw error;
  }
};

/**
 * Add an email to a project
 * @param {Object} params - Parameters for adding an email to a project
 * @param {string} params.emailId - ID of the email to add
 * @param {string} params.vectorStoreId - ID of the vector store
 * @param {function} params.dispatch - Redux dispatch function
 * @param {function} params.chats - Chats array
 * @param {function} params.addEmailFileIdAction - Redux action to add email file ID
 * @param {function} params.updateDigestAction - Redux action to update digest
 * @param {function} params.emailActionData - Email action data
 * @param {function} params.currentDigest - Current digest object
 * @param {function} params.getToken - Get token function
 * @returns {Promise<Object>} Response from the add email to project API
 */
export const addEmailToProject = async ({
  emailId,
  mailIntegrationId,
  vectorStoreId,
  dispatch,
  chats,
  addEmailFileIdAction,
  updateDigestAction,
  emailActionData,
  currentDigest,
  getToken,
}) => {
  try {
    const relatedChat = chats.find(
      (chat) => chat.vectorStoreId === vectorStoreId
    );

    if (!relatedChat) {
      throw new Error("No matching project chat found");
    }

    const token = await getToken();
    const response = await axios
      .post(
        `${API_BASE_URL}/api/email/add_email_to_project`,
        {
          emailId,
          vectorStoreId,
          ...(mailIntegrationId ? { mailIntegrationId } : {}),
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      )
      .then((response) => {
        return response.data;
      });

    const newEmailAction = {
      type: "add-to-project",
      actor: emailActionData.actor || "user",
      reason: emailActionData.reason || "Added to project manually by user",
      metaData: emailActionData.metaData,
    };

    await addEmailActionService(getToken, { emailId, emailActions: newEmailAction, mailIntegrationId });
    await addEmailFileIdService(
      getToken,
      { emailId, chatId: relatedChat.id, fileId: response.emailFileId, mailIntegrationId }
    );

    await new Promise((resolve) => {
      dispatch(
        addEmailFileIdAction({
          emailId,
          chatId: relatedChat.id,
          fileId: response.emailFileId,
          mailIntegrationId,
        })
      );

      dispatch(
        updateDigestAction({
          ...currentDigest,
          content: currentDigest.content.map((item) =>
            item.id === emailId
              ? {
                  ...item,
                  actions: [...(item.actions || []), newEmailAction],
                }
              : item
          ),
        })
      );

      // Give Redux a chance to update the state
      setTimeout(resolve, 100);
    });

    return response.data;
  } catch (error) {
    console.error("Error adding email to project:", error);
    throw error;
  }
};

/**
 * Get assistant actions
 * @param {Array} actions - Array of actions
 * @returns {Array} Array of assistant actions
 */
export const getAssistantActions = (actions) => {
  return actions
    ?.reduce((uniqueActions, action) => {
      if (
        !uniqueActions.some((uniqueAction) => uniqueAction.type === action.type)
      ) {
        uniqueActions.push(action);
      }
      return uniqueActions;
    }, [])
    .filter((action) => action.actor === "assistant");
};

/**
 * Get user actions
 * @param {Array} actions - Array of actions
 * @returns {Array} Array of user actions
 */
export const getUserActions = (actions) => {
  return actions
    ?.filter((action) => action.actor === "user")
    .reduce((uniqueActions, action) => {
      if (
        !uniqueActions.some((uniqueAction) => uniqueAction.type === action.type)
      ) {
        uniqueActions.push(action);
      }
      return uniqueActions;
    }, []);
};

/**
 * Get emails to process
 * Mail the user filed by hand is excluded so this count matches what the
 * backend would actually process, rather than promising work it will skip.
 * @param {Array} emails - Array of emails
 * @param {Array<string>} [configuredDestinationKeys] - Custom Folders & Smart
 *   Email Routing: the mailbox's currently configured destination keys
 *   (system + enabled custom), typically `currentDigest.destinations.map(d => d.key)`.
 *   This is advisory only (the server's fresh owned lookup is authoritative,
 *   per T2/F3) -- omitting it preserves the original system-only default.
 * @returns {Array} Array of emails to process
 */
export const getRemainingEmailsToProcess = (emails, configuredDestinationKeys) => {
  return emails?.filter((email) => {
    const hasAssistantActions =
      email.actions?.filter((action) => action?.actor === "assistant").length >
      0;

    return (
      !email.archived &&
      !email.labels?.includes("SENT") &&
      !hasAssistantActions &&
      !isFiledInTriageDestination(email, configuredDestinationKeys)
    );
  }).map((email) => email.id);
};

/**
 * Get emails to process
 * @param {Array} emails - Array of emails
 * @returns {Array} Array of emails to process
 */
export const getEmailsToProcessCount = (emails) => {
  return emails?.filter((email) => !email.labels?.includes("SENT")).length;
};
